import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, CircularProgress, Link, Paper, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { ExpFormService } from '../../services/expForm.service';
import { ExportInvoiceService } from '../../services/exportInvoice.service';
import { BillOfLadingService } from '../../services/billOfLading.service';
import { PackingListService } from '../../services/packingList.service';
import { UdRegisterService } from '../../services/udRegister.service';
import { ExportLc } from '../../interfaces/commercial.interface';
import { fetchAllPages } from './common/fetchAllPages';

interface DocLink { id: string; label: string; path: string; }
interface Group { title: string; docs: DocLink[]; }

const settle = async <T,>(load: Promise<T[]>): Promise<T[]> => {
    try {
        return await load;
    } catch (e) {
        // A user without access to one document type simply sees no rows for it.
        return [];
    }
};

/**
 * Everything raised against one export LC: EXP forms, UD registers, bills of lading, export
 * invoices and packing lists. Each list is read once and matched on the LC (or on the LC's buyer
 * order for packing lists), so a document type the user cannot open just shows "None".
 */
const LcLinkedDocumentsPanel: React.FC<{ lc: ExportLc }> = ({ lc }) => {
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const [groups, setGroups] = useState<Group[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [exps, uds, bls, invoices, packingLists] = await Promise.all([
                settle(fetchAllPages((page, size) => ExpFormService.list(institutionId, { page, size }))),
                settle(fetchAllPages((page, size) => UdRegisterService.list(institutionId, page, size))),
                settle(fetchAllPages((page, size) => BillOfLadingService.list(institutionId, page, size))),
                settle(fetchAllPages((page, size) => ExportInvoiceService.list(institutionId, { page, size }))),
                settle(fetchAllPages((page, size) => PackingListService.list(institutionId, page, size))),
            ]);
            if (cancelled) return;
            const myInvoices = invoices.filter(i => i.exportLcId === lc.id);
            const invoicePackingListIds = new Set(myInvoices.map(i => i.packingListId).filter(Boolean));
            setGroups([
                { title: 'EXP forms', docs: exps.filter(e => e.exportLcId === lc.id)
                    .map(e => ({ id: e.id, label: e.expNumber, path: `/commercial/exp-form/${e.id}` })) },
                { title: 'UD registers', docs: uds.filter(u => u.exportLcId === lc.id)
                    .map(u => ({ id: u.id, label: u.udNumber, path: `/commercial/ud-register/${u.id}` })) },
                { title: 'Export invoices', docs: myInvoices
                    .map(i => ({ id: i.id, label: i.invoiceNumber, path: `/commercial/export-invoices/${i.id}` })) },
                { title: 'Packing lists', docs: packingLists
                    .filter(p => invoicePackingListIds.has(p.id) || (!!lc.salesOrderId && p.buyerOrderId === lc.salesOrderId))
                    .map(p => ({ id: p.id, label: p.packingListNumber, path: `/commercial/packing-lists/${p.id}` })) },
                { title: 'Bills of lading', docs: bls.filter(b => b.exportLcId === lc.id)
                    .map(b => ({ id: b.id, label: b.blNumber, path: `/commercial/bills-of-lading/${b.id}` })) },
            ]);
        })();
        return () => { cancelled = true; };
    }, [institutionId, lc.id, lc.salesOrderId]);

    if (!groups) return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;

    return (
        <Paper sx={{ p: 2 }} data-testid="lc-linked-documents">
            <Typography variant="h6" sx={{ mb: 1 }}>Related documents</Typography>
            {groups.map(g => (
                <Box key={g.title} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.75 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 150px' }}>{g.title}</Typography>
                    {g.docs.length === 0 ? (
                        <Typography variant="body2">None</Typography>
                    ) : g.docs.map(d => (
                        <Link key={d.id} component={RouterLink} to={d.path} underline="hover" variant="body2" sx={{ mr: 2 }}>
                            {d.label}
                        </Link>
                    ))}
                </Box>
            ))}
        </Paper>
    );
};

export default LcLinkedDocumentsPanel;
